import React, { useCallback, useContext, useState } from 'react';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';

import { UserContext } from 'contexts/UserContext';
import { PaginationControls, ResultsCounter } from 'shared/components/pagination';
import { useOosLogEventsQuery } from 'shared/hooks/warehouse/useOosLogEventsQuery';

import { OosLogFilters } from './OosLogFilters';
import { validateAndBuildAppliedFilters } from './oosLogFilterUtils';
import { OosLogTable } from './OosLogTable';

export const DEFAULT_OOS_LOG_PAGE = 1;
export const DEFAULT_OOS_LOG_PAGE_SIZE = 50;

/** Stable empty object so filters don't remount on every render. */
const EMPTY_FILTER_VALUES = Object.freeze({});

/**
 * Warehouse OOS audit log page. Reads the current user's facility from UserContext.
 */
export function OosLogPage() {
    const { user } = useContext(UserContext);
    const facilityId = user?.facilityID;

    const [page, setPage] = useState(DEFAULT_OOS_LOG_PAGE);
    const [pageSize, setPageSize] = useState(DEFAULT_OOS_LOG_PAGE_SIZE);
    const [filterValues, setFilterValues] = useState(EMPTY_FILTER_VALUES);
    const [appliedFilters, setAppliedFilters] = useState({});
    const [filterError, setFilterError] = useState(null);
    const [pageFacilityId, setPageFacilityId] = useState(facilityId);

    // When facility changes, reset pagination on this render so we never request an out-of-range page.
    let effectivePage = page;
    if (facilityId !== pageFacilityId) {
        setPageFacilityId(facilityId);
        setPage(DEFAULT_OOS_LOG_PAGE);
        effectivePage = DEFAULT_OOS_LOG_PAGE;
    }

    const query = useOosLogEventsQuery({
        end: appliedFilters.end,
        externalSku: appliedFilters.externalSku,
        facilityId,
        page: effectivePage,
        pageSize,
        start: appliedFilters.start,
    });

    const events = query.data?.events ?? [];
    const pagination = query.data?.pagination;
    const total = pagination?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const showLoading = query.isLoading || (query.isFetching && !query.data);
    const showTable = !showLoading && !query.isError && events.length > 0;
    const showEmpty = !showLoading && !query.isError && query.isSuccess && events.length === 0;

    const handleFilterChange = useCallback((nextValues) => {
        setFilterValues(nextValues);
        const { appliedFilters: nextAppliedFilters, error } = validateAndBuildAppliedFilters(nextValues);
        if (error) {
            setFilterError(error);
            return;
        }

        setFilterError(null);
        setAppliedFilters(nextAppliedFilters);
        setPage(DEFAULT_OOS_LOG_PAGE);
    }, []);

    const handlePreviousPage = useCallback(() => {
        setPage((current) => Math.max(DEFAULT_OOS_LOG_PAGE, current - 1));
    }, []);

    const handleNextPage = useCallback(() => {
        setPage((current) => current + 1);
    }, []);

    const handlePageSizeChange = useCallback((nextPageSize) => {
        setPageSize(nextPageSize);
        setPage(DEFAULT_OOS_LOG_PAGE);
    }, []);

    const loadedCount = events.length;

    return (
        <div className="p-4">
            <h1 className="mb-4 text-2xl font-semibold">Out of Stock Log</h1>

            <div className="mb-4">
                <OosLogFilters initialValues={filterValues} onChange={handleFilterChange} />
            </div>

            {filterError ? (
                <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" role="alert">
                    {filterError}
                </div>
            ) : null}

            {!showLoading && query.isSuccess ? (
                <ResultsCounter
                    className="mb-2"
                    isRefreshing={query.isFetching}
                    loadedCount={loadedCount}
                    totalCount={total}
                />
            ) : null}

            {showLoading ? (
                <div
                    aria-label="Loading OOS log events"
                    aria-live="polite"
                    className="flex items-center gap-2 py-8 text-muted-foreground"
                    role="status"
                >
                    <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin" />
                    <span>Loading OOS log events</span>
                </div>
            ) : null}

            {query.isError ? (
                <p className="py-8 text-destructive" role="alert">
                    {query.error?.message ?? 'Failed to load OOS log events'}
                </p>
            ) : null}

            {showEmpty ? (
                <p className="py-8 text-muted-foreground">No OOS log events found for the selected filters.</p>
            ) : null}

            {showTable ? (
                <>
                    <OosLogTable events={events} />
                    <PaginationControls
                        currentPage={effectivePage}
                        hasNext={effectivePage < totalPages}
                        hasPrevious={effectivePage > DEFAULT_OOS_LOG_PAGE}
                        isLoading={query.isFetching}
                        onNext={handleNextPage}
                        onPageSizeChange={handlePageSizeChange}
                        onPrevious={handlePreviousPage}
                        pageSize={pageSize}
                        totalPages={totalPages}
                    />
                </>
            ) : null}
        </div>
    );
}
